import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { copyMessageToClipboard } from '@src/helpers';
import { useQuickInviteLink } from '@src/hooks';
import { fetchWebinarAttendees } from '@src/api/endpoints/webinar';
import { queryKeys } from '@src/utils/queryKeys';
import { useRoomStore } from '@src/stores';
import { usePeersStore } from '@src/stores/peers.store';
import { EUserType } from '@src/types';
import { IWebinarAttendees } from '@src/webinar/types';
import { downloadQRCode } from '@src/utils';
import { getUrlWithQueryParams } from '@src/utils/general.utils';

// Constants
const QR_CODE_SIZE = 235;

const usePanelistInviteModal = () => {
  const [isInviteOpen, setIsInviteOpen] = useState(false);
  const [isQRView, setIsQRView] = useState(false);

  const [isGeneratingCoHost, setIsGeneratingCoHost] = useState(false);
  const [isGeneratingSpeaker, setIsGeneratingSpeaker] = useState(false);
  const [generatedCoHostLink, setGeneratedCoHostLink] = useState<string | null>(null);
  const [generatedSpeakerLink, setGeneratedSpeakerLink] = useState<string | null>(null);
  const [invitesRemaining, setInvitesRemaining] = useState<number | null>(null);
  const [invitesLimit, setInvitesLimit] = useState<number | null>(null);

  const meetingLink = getUrlWithQueryParams(['wid']);
  const roomId = useRoomStore((state) => state.roomId);
  const roomTitle = useRoomStore((state) => state.roomTitle);
  const isHostUser = usePeersStore((state) => state.isHostUser);

  const { mutateAsync: requestQuickInvite } = useQuickInviteLink();

  const attendeesQueryKey = useMemo(
    () => (roomId ? queryKeys.webinar.attendees(roomId) : ['panelist-invite-attendance']),
    [roomId]
  );

  const { data: attendanceData, refetch: refetchAttendance } = useQuery<IWebinarAttendees>({
    queryKey: attendeesQueryKey,
    queryFn: () => fetchWebinarAttendees(roomId!),
    enabled: isInviteOpen && !!roomId && isHostUser,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (!attendanceData) return;
    const panelistsLimit = attendanceData.package?.panelistsLimit ?? 0;
    const speakerCount = attendanceData.panelList?.speakers ?? 0;
    const coHostCount = attendanceData.panelList?.co_hosts ?? 0;
    const remaining = Math.max(panelistsLimit - (speakerCount + coHostCount), 0);
    setInvitesLimit(panelistsLimit);
    setInvitesRemaining(remaining);
  }, [attendanceData]);

  const handleCopyLink = useCallback(
    (link: string = meetingLink) => {
      copyMessageToClipboard(link, 'Link copied');
    },
    [meetingLink]
  );

  const handleOpenQr = useCallback(() => {
    setIsQRView(true);
  }, []);

  const handleBackToInvite = useCallback(() => {
    setIsQRView(false);
  }, []);

  const handleDownloadQR = useCallback(() => {
    if (!meetingLink) return;

    const qrCodeFileName = `${roomTitle}_Webinar_QR.png`;
    downloadQRCode(meetingLink, { size: QR_CODE_SIZE, filename: qrCodeFileName });
  }, [meetingLink, roomTitle]);

  const handleOpenMail = useCallback(() => {
    console.log('Open Mail for:', meetingLink);
  }, [meetingLink]);

  const handleGenerateCoHostLink = useCallback(async () => {
    if (!isHostUser || !roomId) {
      return null;
    }

    if (invitesRemaining === null || invitesRemaining <= 0) {
      return null;
    }

    setIsGeneratingCoHost(true);
    try {
      const response = await requestQuickInvite({ roomId, userType: EUserType.CO_HOST });
      if (response?.success === false) {
        return null;
      }
      const link = response?.link ?? null;
      if (link) {
        setGeneratedCoHostLink(link);
      }
      refetchAttendance();
      return link;
    } finally {
      setIsGeneratingCoHost(false);
    }
  }, [invitesRemaining, requestQuickInvite, roomId, refetchAttendance, isHostUser]);

  const handleGenerateSpeakerLink = useCallback(async () => {
    if (!isHostUser || !roomId) {
      return null;
    }

    if (invitesRemaining === null || invitesRemaining <= 0) {
      return null;
    }

    setIsGeneratingSpeaker(true);
    try {
      const response = await requestQuickInvite({ roomId, userType: EUserType.SPEAKER });
      if (response?.success === false) {
        return null;
      }
      const link = response?.link ?? null;
      if (link) {
        setGeneratedSpeakerLink(link);
      }
      refetchAttendance();
      return link;
    } finally {
      setIsGeneratingSpeaker(false);
    }
  }, [invitesRemaining, requestQuickInvite, roomId, refetchAttendance, isHostUser]);

  const handleInviteModalToggle = useCallback(() => {
    setIsInviteOpen((prev) => {
      // Reset QR view when closing modal
      if (prev) {
        setIsQRView(false);
      }
      return !prev;
    });
  }, []);

  return {
    isInviteOpen,
    setIsInviteOpen,
    handleInviteModalToggle,

    isQRView,
    meetingLink,
    invitesRemaining,
    invitesLimit,
    isGeneratingCoHost,
    isGeneratingSpeaker,
    generatedCoHostLink,
    generatedSpeakerLink,

    handleCopyLink,
    handleOpenQr,
    handleBackToInvite,
    handleDownloadQR,
    handleOpenMail,
    handleGenerateCoHostLink,
    handleGenerateSpeakerLink,
  };
};

export type PanelistInviteModalState = ReturnType<typeof usePanelistInviteModal>;

export default usePanelistInviteModal;
